import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Table,
  Tbody,
  Text,
  useToast,
} from '@chakra-ui/react'
import type { Error, OfferAddon } from 'BrokerTsLib/src/dtos'
import { Insurer, SelectionType } from 'BrokerTsLib/src/enums'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { useSendContractEmail } from '@/hooks/useMailing'
import { SelectArrowDown } from '@/icons'

import { FormLabel } from '../../components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { Loading } from '../../components/Loading'
import { useSelections } from '../../hooks'
import { useGetInsurance } from '../../hooks/useInsuranceParams'
import { useCreateContractAdditionally } from '../../hooks/useWizard'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { getLogo } from '../../utils/companiesLogos'
import { CancelContract } from '../CancelContract'
import { ReactivateContract } from '../ReactivateContract'
import { SendContractContextProvider, SendContractModal, useSendContractContext } from '../SendContractModal'
import { ContractEmailHistory } from './ContractEmailHistory'
import { InsuranceDownloads } from './InsuranceDownloads'
import { RecapTableRow } from './Recap'
import { RecapExtraInsuranceTableRow } from './RecapExtraInsurance'

export const RecapExtraInsuranceSingle = ({ offerAddon }: { offerAddon: OfferAddon }) => {
  const { insuranceParamsId, insuranceParams } = useContext(insuranceParamsContext)
  const { data: dataFs } = useSelections(SelectionType.FormaSjednani)
  const { data: dataInsurance, refetch: refetchInsurance, isLoading: isLoadingInsurance } = useGetInsurance(offerAddon.offerId, { enabled: false })
  const { mutateAsync: sendContractEmail, isLoading: isSendingContractEmail } = useSendContractEmail()
  const { mutateAsync: createContract, isLoading: isCreatingContract } = useCreateContractAdditionally()

  const [priceChanged, setPriceChanged] = useState(false)
  const [alerts, setAlerts] = useState<Error[]>([])

  const [error, setError] = useState<string | null>(null)
  const toast = useToast()

  const insurance = dataInsurance?.data
  const fsEnum = dataFs ?? []
  const isContracted = !!insurance?.contractNumber

  useEffect(() => {
    refetchInsurance()
  }, [])

  const onContractChange = () => {
    refetchInsurance()
  }

  const handleSendContractEmailClick = () => {
    sendContractEmail({
      paramsId: insuranceParamsId!,
      insuranceId: insurance?.insuranceId
    })
      .then(() => {
        toast({
          title: 'Email byl úspěšně odeslán.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
      })
      .catch(() => {
        setError('Při odesílání došlo k chybě')
      })
  }

  const handleCreateContractClick = () => {
    createContract({
      paramsId: insuranceParamsId!,
      offerId: offerAddon.offerId,
      acceptChangedPrice: false
    })
      .then((resp) => {
        if (resp.data.priceChanged)
          setPriceChanged(true)
        else if (resp.data.errors.length > 0)
          setAlerts(resp.data.errors)
        else {
          toast({
            title: 'Smlouva byla sjednána.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
          refetchInsurance()
        }
      })
      .catch(() => {
        setError('Při sjednání došlo k chybě')
      })
  }

  return (
    <FormSection variant={(insurance?.contractCanceled ?? false) ? 'red' : 'default'}>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace doplňkového pojištění</FormSectionTitle>

        <FormSectionDescription>
          <Image src={getLogo(offerAddon.insurerId)} alt={offerAddon.insurerName} width="64px" maxH="48px" />
        </FormSectionDescription>
      </FormSectionHeader>

      <FormSectionBody>
        <Grid sx={{ w: '100%' }}>
          <RecapExtraInsuranceTableRow addonOffer={offerAddon} />
        </Grid>

        <Divider />

        {!isLoadingInsurance && insurance?.contractNumber &&
          <FormLabel>Údaje o smlouvě doplňkového pojištění</FormLabel>
        }

        {isLoadingInsurance && <Loading large={true} />}

        {!isLoadingInsurance && !insurance?.contractNumber &&
          <Stack direction='row' w='100%'>
            <Button type="button" colorScheme="primary" onClick={handleCreateContractClick} isLoading={isCreatingContract}>
              Sjednat pojištění
            </Button>

            {(priceChanged || alerts.length > 0) &&
              <Alert colorScheme="red" mb={8}>
                <Stack direction='column'>
                  {priceChanged && <Text>
                    Před sjednáním došlo ke změně ceny. Vraťte se na
                    <Link as={RouterLink} colorScheme="blue" to={`/carkulka/extra/wizard/2/${insuranceParamsId!}`}>&nbsp;seznam kalkulací&nbsp;</Link>
                    a vybranou nabídku překalkulujte.
                  </Text>}

                  {alerts.length > 0 && alerts.map(alert => (
                    <Text key={alert.message}>{alert.message}</Text>
                  ))}
                </Stack>
              </Alert>
            }
          </Stack>
        }

        {insurance && insurance?.contractNumber &&
          <>
            <Table variant="unstyled" cellPadding={0} cellSpacing={0}>
              <Tbody>
                <RecapTableRow col1="Číslo pojistné smlouvy" col2={insurance?.contractNumber} />
                <RecapTableRow col1="Způsob sjednání" col2={fsEnum.find(x => x.value === insurance.contractAcceptationType?.toString())?.description} />
                <RecapTableRow col1="Datum sjednání" col2={moment(new Date(insurance.contractCreatedDate!)).format('D. M. YYYY HH:mm')} />
                <RecapTableRow col1="Počátek pojištění" col2={
                  offerAddon.insurerId == Insurer.Das
                    ? '1 den po připsání první splátky na účet pojistitele'
                    : moment(new Date(insurance.insuranceStartDate!)).format('D. M. YYYY HH:mm')
                } />
                {insurance.contractCanceledDate &&
                  <RecapTableRow col1={<Text fontWeight='bold'>Smlouva byla stornována</Text>} col2={`${moment(new Date(insurance.contractCanceledDate!)).format('D. M. YYYY HH:mm')}, ${insurance.contractCanceledBy}`} />
                }
              </Tbody>
            </Table>

            {offerAddon.infos.length > 0 &&
              <Alert colorScheme="orange">
                <Text as="p">
                  {offerAddon.infos.map((info, i) => (
                    <React.Fragment key={info.message ?? i}>
                      <Text as="span" dangerouslySetInnerHTML={{ __html: info.message }}></Text>
                      <br />
                    </ React.Fragment>
                  ))}
                </Text>
              </Alert>
            }

            {insurance.cancellationOptions?.canCancel &&
              <CancelContract
                callback={onContractChange}
                insuranceId={insurance.insuranceId!}
              />
            }
            {insurance.cancellationOptions?.canReactivate &&
              <ReactivateContract
                callback={onContractChange}
                insuranceId={insurance.insuranceId!}
              />
            }

            <Divider />

            <SendContractContextProvider insuranceId={insurance.insuranceId ?? undefined}>
              <Box sx={{ display: 'grid', gap: 8, placeItems: 'start' }}>
                <Box sx={{ display: 'grid', gap: 4, placeItems: 'start' }}>
                  <FormLabel>Dokumenty a informace k doplňkovému pojištění</FormLabel>

                  <Stack direction='row' w='100%' alignItems={'end'} >
                    <SendContractModal />

                    <ButtonGroup isAttached variant="solid">
                      <Button type="button" colorScheme="primary" onClick={handleSendContractEmailClick} isLoading={isSendingContractEmail}>
                        Odeslat e-mail klientovi
                      </Button>
                      <Menu>
                        <MenuButton as={IconButton} icon={<SelectArrowDown boxSize={3} />} px={2} />
                        <MenuList>
                          <SendContactEmailMenuItem />
                        </MenuList>
                      </Menu>
                    </ButtonGroup>
                    {error && <Alert colorScheme="red" w='auto'>{error}</Alert>}
                  </Stack>

                  <InsuranceDownloads paramsId={insuranceParamsId!} insurerOfferId={insurance.selectedOfferId!} />
                </Box>
              </Box>

              {insuranceParams?.generalParams.paramsId && (
                <ContractEmailHistory
                  paramsId={insuranceParams?.generalParams.paramsId}
                  insurerOfferId={insurance.selectedOfferId!}
                />
              )}
            </SendContractContextProvider>
          </>
        }
      </FormSectionBody>
    </FormSection>
  )
}

const SendContactEmailMenuItem = () => {
  const { onOpen } = useSendContractContext()
  return <MenuItem gap={3} onClick={onOpen}>
    Zobrazit a odeslat na e-mail
  </MenuItem>
}